import React, { useState, useEffect } from 'react';
import Header from '../../components/Header';
import Sidebar from '../../components/Sidebar';
import { Calculator, Clock, Copy, Trash2, Plus, Minus, AlertCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { vatCalculatorService } from '../../services/vatCalculatorService';
import { formatCurrency } from '../../utils/currencyFormatter';

const VATTaxCalculator = () => {
  const { profile } = useAuth();
  const [amount, setAmount] = useState('');
  const [vatPercentage, setVatPercentage] = useState('15');
  const [calculationType, setCalculationType] = useState('add');
  const [history, setHistory] = useState([]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const currency = profile?.currency || 'ZAR';

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    try {
      setLoading(true);
      const data = await vatCalculatorService?.getAll();
      setHistory(data);
    } catch (err) {
      setError(err?.message || 'Failed to load history');
    } finally {
      setLoading(false);
    }
  };

  const parseNum = (val) => {
    const num = parseFloat(String(val)?.replace(/[^\d.-]/g, ''));
    return isNaN(num) ? 0 : num;
  };

  const handleCalculate = async () => {
    const baseAmount = parseNum(amount);
    const vatRate = parseNum(vatPercentage) / 100;

    if (baseAmount <= 0) return;

    let grossAmount, vatAmount, netAmount;

    if (calculationType === 'add') {
      netAmount = baseAmount;
      vatAmount = baseAmount * vatRate;
      grossAmount = baseAmount + vatAmount;
    } else {
      grossAmount = baseAmount;
      netAmount = baseAmount / (1 + vatRate);
      vatAmount = baseAmount - netAmount;
    }

    const calculationResult = {
      timestamp: new Date()?.toLocaleTimeString(),
      date: new Date()?.toLocaleDateString(),
      type: calculationType,
      baseAmount,
      vatPercentage: parseNum(vatPercentage),
      grossAmount,
      vatAmount,
      netAmount,
    };

    setResult(calculationResult);
    
    // Save to Supabase
    try {
      setSaving(true);
      await vatCalculatorService?.create({
        baseAmount: calculationResult?.baseAmount,
        vatPercentage: calculationResult?.vatPercentage,
        calculationType: calculationResult?.type,
        grossAmount: calculationResult?.grossAmount,
        vatAmount: calculationResult?.vatAmount,
        netAmount: calculationResult?.netAmount,
      });
      await loadHistory();
    } catch (err) {
      setError(err?.message || 'Failed to save calculation');
    } finally {
      setSaving(false);
    }
  };

  const handlePresetVAT = (percentage) => {
    setVatPercentage(percentage?.toString());
  };

  const copyToClipboard = (text) => {
    navigator.clipboard?.writeText(text);
  };

  const deleteHistoryItem = async (id) => {
    try {
      await vatCalculatorService?.delete(id);
      setHistory(prev => prev?.filter(item => item?.id !== id));
    } catch (err) {
      setError(err?.message || 'Failed to delete calculation');
    }
  };

  const clearHistory = async () => {
    try {
      await vatCalculatorService?.deleteAll();
      setHistory([]);
      setResult(null);
    } catch (err) {
      setError(err?.message || 'Failed to clear history');
    }
  };

  const recalculateFromHistory = (item) => {
    setAmount(item?.baseAmount?.toString());
    setVatPercentage(item?.vatPercentage?.toString());
    setCalculationType(item?.type);
    setResult({
      timestamp: new Date()?.toLocaleTimeString(),
      date: new Date()?.toLocaleDateString(),
      type: item?.type,
      baseAmount: item?.baseAmount,
      vatPercentage: item?.vatPercentage,
      grossAmount: item?.grossAmount,
      vatAmount: item?.vatAmount,
      netAmount: item?.netAmount,
    });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar />
      <Header />
      <main className="ml-64 mt-16 p-6">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
              <Calculator className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">VAT/Tax Calculator</h1>
              <p className="text-sm text-slate-600">Calculate VAT/Tax amounts with instant history tracking</p>
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <p className="text-sm text-red-800">{error}</p>
            <button
              onClick={() => setError('')}
              className="ml-auto text-red-600 hover:text-red-700"
            >
              ×
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Sidebar - Quick Presets */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
              <h3 className="text-lg font-bold text-slate-900 mb-4">Quick VAT Presets</h3>
              <div className="space-y-3">
                <button
                  onClick={() => handlePresetVAT(0)}
                  className="w-full px-4 py-3 rounded-lg border-2 border-slate-200 hover:border-blue-500 hover:bg-blue-50 transition-all text-left"
                >
                  <span className="font-semibold text-slate-900">0%</span>
                  <span className="text-sm text-slate-600 block">Zero-rated</span>
                </button>
                <button
                  onClick={() => handlePresetVAT(15)}
                  className="w-full px-4 py-3 rounded-lg border-2 border-slate-200 hover:border-blue-500 hover:bg-blue-50 transition-all text-left"
                >
                  <span className="font-semibold text-slate-900">15%</span>
                  <span className="text-sm text-slate-600 block">Standard VAT (South Africa)</span>
                </button>
                <button
                  onClick={() => handlePresetVAT(20)}
                  className="w-full px-4 py-3 rounded-lg border-2 border-slate-200 hover:border-blue-500 hover:bg-blue-50 transition-all text-left"
                >
                  <span className="font-semibold text-slate-900">20%</span>
                  <span className="text-sm text-slate-600 block">UK VAT</span>
                </button>
              </div>

              <div className="mt-6 pt-6 border-t border-slate-200">
                <h4 className="text-sm font-semibold text-slate-900 mb-3">Calculation Shortcuts</h4>
                <div className="space-y-2 text-sm text-slate-600">
                  <div className="flex items-center gap-2">
                    <kbd className="px-2 py-1 bg-slate-100 rounded text-xs">Enter</kbd>
                    <span>Calculate</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <kbd className="px-2 py-1 bg-slate-100 rounded text-xs">Ctrl + C</kbd>
                    <span>Copy result</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Center - Calculator */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
              <h3 className="text-lg font-bold text-slate-900 mb-6">Calculator</h3>

              {/* Calculation Type Toggle */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-700 mb-3">Calculation Type</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setCalculationType('add')}
                    className={`px-4 py-3 rounded-lg border-2 transition-all flex items-center justify-center gap-2 ${
                      calculationType === 'add' ?'border-blue-500 bg-blue-50 text-blue-700' :'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <Plus className="w-4 h-4" />
                    <span className="font-medium">Add VAT</span>
                  </button>
                  <button
                    onClick={() => setCalculationType('remove')}
                    className={`px-4 py-3 rounded-lg border-2 transition-all flex items-center justify-center gap-2 ${
                      calculationType === 'remove' ?'border-blue-500 bg-blue-50 text-blue-700' :'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <Minus className="w-4 h-4" />
                    <span className="font-medium">Remove VAT</span>
                  </button>
                </div>
              </div>

              {/* Amount Input */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  {calculationType === 'add' ? 'Net Amount' : 'Gross Amount'}
                </label>
                <input
                  type="text"
                  value={amount}
                  onChange={(e) => setAmount(e?.target?.value)}
                  onKeyPress={(e) => e?.key === 'Enter' && handleCalculate()}
                  className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-lg"
                  placeholder="Enter amount"
                />
              </div>

              {/* VAT Percentage Input */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-700 mb-2">VAT/Tax Percentage</label>
                <div className="relative">
                  <input
                    type="text"
                    value={vatPercentage}
                    onChange={(e) => setVatPercentage(e?.target?.value)}
                    className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-lg pr-10"
                    placeholder="Enter VAT %"
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500">%</span>
                </div>
              </div>

              {/* Calculate Button */}
              <button
                onClick={handleCalculate}
                disabled={saving}
                className="w-full px-6 py-4 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white font-semibold transition-colors flex items-center justify-center gap-2"
              >
                <Calculator className="w-5 h-5" />
                {saving ? 'Saving...' : 'Calculate'}
              </button>

              {/* Result Display */}
              {result && (
                <div className="mt-6 space-y-3">
                  <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium text-green-900">Gross Amount</span>
                      <button
                        onClick={() => copyToClipboard(result?.grossAmount?.toString())}
                        className="text-green-600 hover:text-green-700"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                    <p className="text-2xl font-bold text-green-700">{formatCurrency(result?.grossAmount, currency)}</p>
                  </div>

                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium text-blue-900">VAT Amount</span>
                      <button
                        onClick={() => copyToClipboard(result?.vatAmount?.toString())}
                        className="text-blue-600 hover:text-blue-700"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                    <p className="text-2xl font-bold text-blue-700">{formatCurrency(result?.vatAmount, currency)}</p>
                  </div>

                  <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium text-slate-900">Net Amount</span>
                      <button
                        onClick={() => copyToClipboard(result?.netAmount?.toString())}
                        className="text-slate-600 hover:text-slate-700"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                    <p className="text-2xl font-bold text-slate-900">{formatCurrency(result?.netAmount, currency)}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Sidebar - History */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-slate-600" />
                  <h3 className="text-lg font-bold text-slate-900">History</h3>
                </div>
                {history?.length > 0 && (
                  <button
                    onClick={clearHistory}
                    className="text-sm text-red-600 hover:text-red-700 font-medium"
                  >
                    Clear All
                  </button>
                )}
              </div>

              {loading ? (
                <div className="text-center py-12">
                  <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
                  <p className="text-sm text-slate-500 mt-3">Loading history...</p>
                </div>
              ) : history?.length === 0 ? (
                <div className="text-center py-12">
                  <Calculator className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-sm text-slate-500">No calculations yet</p>
                  <p className="text-xs text-slate-400 mt-1">Your calculation history will appear here</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-[600px] overflow-y-auto">
                  {history?.map((item) => (
                    <div
                      key={item?.id}
                      className="p-4 border border-slate-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-all cursor-pointer"
                      onClick={() => recalculateFromHistory(item)}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            {item?.calculationType === 'add' ? (
                              <Plus className="w-3 h-3 text-green-600" />
                            ) : (
                              <Minus className="w-3 h-3 text-orange-600" />
                            )}
                            <span className="text-xs font-medium text-slate-600">
                              {item?.calculationType === 'add' ? 'Added' : 'Removed'} {item?.vatPercentage}% VAT
                            </span>
                          </div>
                          <p className="text-sm font-bold text-slate-900">{formatCurrency(item?.grossAmount, currency)}</p>
                          <p className="text-xs text-slate-500">
                            {new Date(item?.createdAt)?.toLocaleTimeString()} • {new Date(item?.createdAt)?.toLocaleDateString()}
                          </p>
                        </div>
                        <button
                          onClick={(e) => {
                            e?.stopPropagation();
                            deleteHistoryItem(item?.id);
                          }}
                          className="text-slate-400 hover:text-red-600 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="text-xs text-slate-600 space-y-1">
                        <div className="flex justify-between">
                          <span>VAT Amount:</span>
                          <span className="font-medium">{formatCurrency(item?.vatAmount, currency)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Net Amount:</span>
                          <span className="font-medium">{formatCurrency(item?.netAmount, currency)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default VATTaxCalculator;